import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, User, Users, List, LogIn, UserCheck, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { isClientLoggedIn } from "../../client/ClientAuthContext";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { getApiBaseUrl } from "../../../publicSessionService";
import { useTranslation } from "react-i18next";
import { WidgetLanguageSelector } from "../../../components/WidgetLanguageSelector";
import { PromoPills } from "../../../look/PromoPills";
import { getWidgetLook, mediaUrl } from "../../../look/widgetLook";
import { TOP_PILL } from "../../../look/topPill";

export default function HomeView({
  onChooseSpecialist,
  onChooseServices,
  onOpenClientDashboard,
  onOpenSupport,
}: {
  onChooseSpecialist: () => void;
  onChooseServices: () => void;
  onOpenClientDashboard?: () => void;
  onOpenSupport?: () => void;
}) {
  const { t } = useTranslation();
  // The salon's header image over the top of the screen (Settings → Online booking)
  const header = getWidgetLook()?.header_image ?? null;
  // solid pills in the salon's colour, with or without the photo (look/topPill.ts)
  const topButton = TOP_PILL;

  const bookingOptions = [
    {
      id: "specialist",
      title: t("widget.specialists.choose", "Choose specialist"),
      icon: <Users className="w-5 h-5" />,
      onClick: onChooseSpecialist,
    },
    {
      id: "services",
      title: t("widget.services.choose", "Choose services"),
      icon: <List className="w-5 h-5" />,
      onClick: onChooseServices,
    },
  ];

  return (
    <div className="bg-background h-[100dvh] min-h-[100dvh]">
      <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
        <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden relative overflow-y-auto">
          {header && (
            <div className="relative w-full h-52 sm:h-56 shrink-0 bg-muted" data-testid="widget-header-image">
              <img src={mediaUrl(header)} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          {/* Top Header Buttons */}
          <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-start">
            <div className="flex items-center gap-2">
              {onOpenSupport && (
                <button
                  className={cn("flex items-center gap-2 px-3 py-2 rounded-full transition-colors cursor-pointer", topButton)}
                  onClick={onOpenSupport}
                  title={t("widget.support.need_help", "Need help?")}
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">{t("widget.support.label", "Help")}</span>
                </button>
              )}
              <WidgetLanguageSelector buttonClassName={topButton} />
            </div>

            {/* Client Dashboard Button - Right */}
            {onOpenClientDashboard && getCompanyRegistry().getSettings()?.client_account_enabled === true && (
              <button
                className={cn("flex items-center gap-2 px-3 py-2 rounded-full transition-colors cursor-pointer", topButton)}
                onClick={onOpenClientDashboard}
                title={t("widget.client.dashboard", "My account")}
              >
                {isClientLoggedIn() ? (
                  <UserCheck className="w-5 h-5" />
                ) : (
                  <LogIn className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">{t("widget.client.dashboard", "My account")}</span>
              </button>
            )}
          </div>

          {/* Main centered content */}
          <div className={cn("flex-1 flex justify-center p-6", header ? "items-start pt-0" : "items-center")}>
            <div className="w-full max-w-md">
              {/* Header Section */}
              <div className="p-6 pb-4 text-center">
                {/* Profile Avatar */}
                <div className={cn("w-20 h-20 mx-auto mb-4 relative", header && "-mt-16 z-10")}>
                  <div className={cn("w-full h-full rounded-full overflow-hidden", header && "ring-4 ring-card bg-card")}>
                    {getCompanyRegistry().getSettings()?.logo ? (
                      <img
                        src={`${getApiBaseUrl()}${getCompanyRegistry().getSettings()?.logo}`}
                        alt={getCompanyRegistry().getSettings()?.public_title ?? t("widget.common.logo", "Logo")}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-600 flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Business Name */}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h1 className="text-2xl font-semibold text-foreground">{getCompanyRegistry().getSettings()?.public_title ?? ""}</h1>
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>

              {/* The salon's promos — a tap opens the picture and the text */}
              <PromoPills className="mb-4" />

              {/* Booking Options */}
              <div className="px-1 pb-1 space-y-3">
                {bookingOptions.map((option) => (
                  <Button
                    key={option.id}
                    variant="ghost"
                    className={cn(
                      "w-full h-14 justify-start gap-4 rounded-xl bg-muted/50 hover:bg-muted/80 transition-all duration-200",
                      "border border-transparent hover:border-border/50",
                      "text-left font-medium text-base",
                    )}
                    onClick={option.onClick}
                  >
                    <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center ios-shadow">
                      {option.icon}
                    </div>
                    <span className="flex-1">{option.title}</span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground rotate-[-90deg]" />
                  </Button>
                ))}
              </div>

              {/* Footer */}
              <div className="px-6 pb-6 pt-2">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-medium">
                    <a href="https://boona.app" target="_blank">
                      {t("widget.home.powered_by", "Powered by")} <span className="font-semibold text-foreground">Boona</span>
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* end main content */}
        </Card>
      </div>
    </div>
  );
}
